import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import ErrorBoundary from '../components/common/ErrorBoundary';
import {
  Box,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider,
  Chip,
  Tooltip,
  Collapse,
  ListItemButton,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import EventNoteIcon from '@mui/icons-material/EventNote';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import HearingIcon from '@mui/icons-material/Hearing';
import CreateIcon from '@mui/icons-material/Create';
import ArticleIcon from '@mui/icons-material/Article';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import SchoolIcon from '@mui/icons-material/School';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import FeedbackIcon from '@mui/icons-material/Feedback';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PeopleIcon from '@mui/icons-material/People';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import QuizIcon from '@mui/icons-material/Quiz';
import RateReviewIcon from '@mui/icons-material/RateReview';
import TimerIcon from '@mui/icons-material/Timer';
import DevicesIcon from '@mui/icons-material/Devices';
import ReceiptIcon from '@mui/icons-material/Receipt';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import HandshakeIcon from '@mui/icons-material/Handshake';
import GroupsIcon from '@mui/icons-material/Groups';
import { useLayout } from '../contexts/LayoutContext';
import { useAuth } from '../contexts/AuthContext';
import { useAdmin } from '../contexts/AdminContext';
import { useToast } from '../contexts/ToastContext';
import { useDarkMode } from '../contexts/DarkModeContext';

const drawerWidth = 280;

const AdminLayout = () => {
  const { sidebarOpen, toggleSidebar } = useLayout();
  const { user, logout } = useAuth();
  const { isPlatformAdmin, getRoleLabel } = useAdmin();
  const { showToast } = useToast();
  const { mode, toggleDarkMode, isDark } = useDarkMode();
  const location = useLocation();

  const [openSections, setOpenSections] = useState({
    management: true,
    partners: false,
    content: false,
    answers: false,
    feedback: false,
  });

  const handleLogout = async () => {
    try {
      await logout();
      showToast('Logged out successfully', 'success');
    } catch (error) {
      showToast('Error during logout', 'error');
    }
  };

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Dynamic page title based on route
  const getPageTitle = (pathname) => {
    const routeTitles = {
      '/admin-dashboard': 'Admin Dashboard',
      '/users-management': 'Users Management',
      '/registrations-management': 'Registrations Management',
      '/scores-management': 'Scores Management',
      '/speaking-sessions': 'Speaking Sessions',
      '/referrals-management': 'Referrals Management',
      '/product-sessions': 'Product Sessions',
      '/sessions-management': 'Login Sessions',
      '/invoices-management': 'Invoices Management',
      '/partner-students': 'Partner Students',
      '/partner-members': 'Team Members',
      '/exams': 'Exams Management',
      '/readings': 'Readings Management',
      '/listenings': 'Listenings Management',
      '/writings': 'Writings Management',
      '/speakings': 'Speakings Management',
      '/reading-answers': 'Reading Answers',
      '/listening-answers': 'Listening Answers',
      '/writing-answers': 'Writing Answers',
      '/speaking-feedback': 'Speaking Feedback',
      '/feedbacks': 'Feedbacks Management',
      '/blogs': 'Blogs Management',
      '/instructors': 'Instructors Management',
    };
    return routeTitles[pathname] || 'Examinax Control Panel';
  };

  // Update document title when route changes
  useEffect(() => {
    const pageTitle = getPageTitle(location.pathname);
    document.title = `${pageTitle} | Examinax Control Panel`;
  }, [location.pathname]);

  const navigationCategories = [
    {
      title: 'Dashboard',
      items: [
        {
          text: 'Admin Dashboard',
          icon: <AdminPanelSettingsIcon />,
          path: '/admin-dashboard',
        },
      ],
    },
    {
      title: 'Management',
      key: 'management',
      icon: <ManageAccountsIcon />,
      items: [
        {
          text: 'Exams',
          icon: <EventNoteIcon />,
          path: '/exams',
        },
        {
          text: 'Registrations',
          icon: <AssignmentIcon />,
          path: '/registrations-management',
        },
        {
          text: 'Scores',
          icon: <AssessmentIcon />,
          path: '/scores-management',
        },
        {
          text: 'Users',
          icon: <PeopleIcon />,
          path: '/users-management',
          platformOnly: true,
        },
        {
          text: 'Referrals',
          icon: <CardGiftcardIcon />,
          path: '/referrals-management',
          platformOnly: true,
        },
        {
          text: 'Product Sessions',
          icon: <TimerIcon />,
          path: '/product-sessions',
          platformOnly: true,
        },
        {
          text: 'Login Sessions',
          icon: <DevicesIcon />,
          path: '/sessions-management',
          platformOnly: true,
        },
        {
          text: 'Invoices',
          icon: <ReceiptIcon />,
          path: '/invoices-management',
          platformOnly: true,
        },
        {
          text: 'Speaking Sessions',
          icon: <RecordVoiceOverIcon />,
          path: '/speaking-sessions',
          platformOnly: true,
        },
      ],
    },
    {
      title: 'Partners',
      key: 'partners',
      icon: <HandshakeIcon />,
      items: [
        {
          text: 'Partner Students',
          icon: <SchoolIcon />,
          path: '/partner-students',
        },
        {
          text: 'Team Members',
          icon: <GroupsIcon />,
          path: '/partner-members',
        },
      ],
    },
    {
      title: 'Student Answers',
      key: 'answers',
      icon: <AssessmentIcon />,
      items: [
        {
          text: 'Reading Answers',
          icon: <MenuBookIcon />,
          path: '/reading-answers',
        },
        {
          text: 'Listening Answers',
          icon: <HearingIcon />,
          path: '/listening-answers',
        },
        {
          text: 'Writing Answers',
          icon: <CreateIcon />,
          path: '/writing-answers',
        },
      ],
    },
    {
      title: 'Exam Content',
      key: 'content',
      icon: <QuizIcon />,
      platformOnly: true,
      items: [
        {
          text: 'Readings',
          icon: <MenuBookIcon />,
          path: '/readings',
        },
        {
          text: 'Listenings',
          icon: <HearingIcon />,
          path: '/listenings',
        },
        {
          text: 'Writings',
          icon: <CreateIcon />,
          path: '/writings',
        },
        {
          text: 'Speakings',
          icon: <RecordVoiceOverIcon />,
          path: '/speakings',
        },
      ],
    },
    {
      title: 'Feedback',
      key: 'feedback',
      icon: <RateReviewIcon />,
      platformOnly: true,
      items: [
        {
          text: 'Feedbacks',
          icon: <RateReviewIcon />,
          path: '/feedbacks',
        },
        {
          text: 'Speaking Feedback',
          icon: <RecordVoiceOverIcon />,
          path: '/speaking-feedback',
        },
      ],
    },
    {
      title: 'Other',
      platformOnly: true,
      items: [
        {
          text: 'Blogs',
          icon: <ArticleIcon />,
          path: '/blogs',
        },
        {
          text: 'Instructors',
          icon: <SchoolIcon />,
          path: '/instructors',
        },
      ],
    },
  ];

  // Filter categories and items based on platform admin status
  const getVisibleCategories = () => {
    return navigationCategories
      .filter(cat => isPlatformAdmin || !cat.platformOnly)
      .map(cat => ({
        ...cat,
        items: cat.items.filter(item => isPlatformAdmin || !item.platformOnly)
      }))
      .filter(cat => cat.items.length > 0);
  };

  const visibleCategories = getVisibleCategories();

  const isActiveRoute = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      
      {/* Modern AppBar */}
      <AppBar
        position="fixed"
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'background.paper',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'grey.200',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              color="inherit"
              aria-label="toggle drawer"
              onClick={toggleSidebar}
              sx={{
                mr: 2,
                color: 'text.primary',
                '&:hover': {
                  backgroundColor: 'grey.100',
                }
              }}
            >
              <MenuIcon />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <img
                src="/logo_main.svg"
                alt="Examinax Logo"
                style={{
                  height: '40px',
                  width: 'auto',
                }}
              />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Examinax Control Panel
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  Exam Management System
                </Typography>
              </Box>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Tooltip title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
              <IconButton
                onClick={toggleDarkMode}
                sx={{
                  color: 'text.primary',
                  '&:hover': {
                    backgroundColor: isDark ? 'grey.100' : 'grey.100',
                  },
                }}
              >
                {isDark ? <Brightness7Icon /> : <Brightness4Icon />}
              </IconButton>
            </Tooltip>
            <Chip
              label={getRoleLabel()}
              size="small"
              color={isPlatformAdmin ? 'error' : 'primary'}
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
            <Avatar
              sx={{
                width: 32,
                height: 32,
                backgroundColor: 'primary.main',
                fontSize: '0.875rem',
                fontWeight: 600
              }}
            >
              {user?.name ? user.name.charAt(0).toUpperCase() : 'A'}
            </Avatar>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Modern Sidebar */}
      <Drawer
        variant="persistent"
        anchor="left"
        open={sidebarOpen}
        sx={{
          width: sidebarOpen ? drawerWidth : 0,
          flexShrink: 0,
          transition: 'width 0.3s ease',
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            backgroundColor: 'background.paper',
            borderRight: '1px solid',
            borderColor: 'grey.200',
            boxShadow: '4px 0 6px -1px rgba(0, 0, 0, 0.1), 2px 0 4px -1px rgba(0, 0, 0, 0.06)',
          },
        }}
      >
        <Toolbar />
        
        {/* Sidebar Header */}
        <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'grey.200' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
            Navigation
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Manage your exam content
          </Typography>
        </Box>

        {/* Navigation Items */}
        <Box sx={{ flexGrow: 1, overflow: 'auto', py: 1 }}>
          <List sx={{ px: 2 }}>
            {visibleCategories.map((category, categoryIndex) => (
              <Box key={category.title}>
                {category.key ? (
                  // Collapsible category
                  <>
                    <ListItemButton
                      onClick={() => toggleSection(category.key)}
                      sx={{
                        borderRadius: 2,
                        mb: 0.5,
                        backgroundColor: 'grey.50',
                        '&:hover': {
                          backgroundColor: 'grey.100',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40, color: 'text.secondary' }}>
                        {category.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={category.title}
                        sx={{
                          '& .MuiListItemText-primary': {
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            color: 'text.primary',
                          },
                        }}
                      />
                      {openSections[category.key] ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                    <Collapse in={openSections[category.key]} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {category.items.map((item) => (
                          <ListItem
                            key={item.text}
                            component={Link}
                            to={item.path}
                            sx={{
                              pl: 4,
                              borderRadius: 2,
                              mb: 0.5,
                              backgroundColor: isActiveRoute(item.path) ? 'primary.50' : 'transparent',
                              border: isActiveRoute(item.path) ? '1px solid' : '1px solid transparent',
                              borderColor: isActiveRoute(item.path) ? 'primary.200' : 'transparent',
                              '&:hover': {
                                backgroundColor: isActiveRoute(item.path) ? 'primary.100' : 'grey.50',
                                borderColor: isActiveRoute(item.path) ? 'primary.300' : 'grey.200',
                              },
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 36,
                                color: isActiveRoute(item.path) ? 'primary.main' : 'text.secondary',
                              }}
                            >
                              {item.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={item.text}
                              sx={{
                                '& .MuiListItemText-primary': {
                                  fontWeight: isActiveRoute(item.path) ? 600 : 500,
                                  color: isActiveRoute(item.path) ? 'primary.main' : 'text.primary',
                                  fontSize: '0.813rem',
                                },
                              }}
                            />
                            {isActiveRoute(item.path) && (
                              <Box
                                sx={{
                                  width: 3,
                                  height: 16,
                                  backgroundColor: 'primary.main',
                                  borderRadius: 2,
                                }}
                              />
                            )}
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </>
                ) : (
                  // Non-collapsible category
                  <>
                    {categoryIndex > 0 && <Divider sx={{ my: 1 }} />}
                    {category.title !== 'Dashboard' && (
                      <Typography
                        variant="caption"
                        sx={{
                          px: 2,
                          py: 1,
                          display: 'block',
                          color: 'text.secondary',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          fontSize: '0.75rem',
                        }}
                      >
                        {category.title}
                      </Typography>
                    )}
                    {category.items.map((item) => (
                      <ListItem
                        key={item.text}
                        component={Link}
                        to={item.path}
                        sx={{
                          borderRadius: 2,
                          mb: 0.5,
                          backgroundColor: isActiveRoute(item.path) ? 'primary.50' : 'transparent',
                          border: isActiveRoute(item.path) ? '1px solid' : '1px solid transparent',
                          borderColor: isActiveRoute(item.path) ? 'primary.200' : 'transparent',
                          '&:hover': {
                            backgroundColor: isActiveRoute(item.path) ? 'primary.100' : 'grey.50',
                            borderColor: isActiveRoute(item.path) ? 'primary.300' : 'grey.200',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 40,
                            color: isActiveRoute(item.path) ? 'primary.main' : 'text.secondary',
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.text}
                          sx={{
                            '& .MuiListItemText-primary': {
                              fontWeight: isActiveRoute(item.path) ? 600 : 500,
                              color: isActiveRoute(item.path) ? 'primary.main' : 'text.primary',
                              fontSize: '0.875rem',
                            },
                          }}
                        />
                        {isActiveRoute(item.path) && (
                          <Box
                            sx={{
                              width: 4,
                              height: 20,
                              backgroundColor: 'primary.main',
                              borderRadius: 2,
                            }}
                          />
                        )}
                      </ListItem>
                    ))}
                  </>
                )}
              </Box>
            ))}
          </List>
        </Box>

        <Divider />

        {/* Sidebar Footer */}
        <Box sx={{ p: 2 }}>
          <List sx={{ p: 0 }}>
            <ListItem
              sx={{
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: 'grey.50',
                },
                transition: 'background-color 0.2s ease',
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'text.secondary' }}>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText
                primary="Settings"
                sx={{
                  '& .MuiListItemText-primary': {
                    fontWeight: 500,
                    fontSize: '0.875rem',
                  },
                }}
              />
            </ListItem>
            <ListItem
              onClick={handleLogout}
              sx={{
                borderRadius: 2,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'error.50',
                },
                transition: 'background-color 0.2s ease',
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'error.main' }}>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText
                primary="Logout"
                sx={{
                  '& .MuiListItemText-primary': {
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    color: 'error.main',
                  },
                }}
              />
            </ListItem>
          </List>
        </Box>
      </Drawer>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{ 
          flexGrow: 1, 
          transition: 'margin 0.3s ease',
          backgroundColor: 'background.default',
          minHeight: '100vh',
        }}
      >
        <Toolbar />
<Box sx={{ p: 3 }}>
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminLayout;
